/**
 * @module animations/scrollReveal
 *
 * A scroll-reveal that cannot strand content.
 *
 * ## The problem this solves
 *
 * Scroll callbacks fire on *transitions*, and `ScrollTrigger` derives those from
 * a scroll position it tracks itself. Three situations on this site broke that
 * reliably enough to leave content at `opacity: 0`:
 *
 *  - a `#hash` deep link, where the browser has already scrolled before the
 *    triggers exist, so a trigger is created "past" its start and never
 *    transitions;
 *  - fast programmatic or trackpad-flick scrolling that jumps a trigger over its
 *    whole range between two updates;
 *  - layout shifts from injected SVG and web fonts, which move triggers between
 *    refreshes.
 *
 * `IntersectionObserver` has none of those failure modes: it reports geometry,
 * it fires immediately for elements that are already visible when observed, and
 * it re-evaluates when layout changes. So the *decision* to reveal is made here
 * by an IntersectionObserver, while the *animation* stays pure GSAP.
 *
 * Elements already above the viewport are moved straight to the end state —
 * there is no scroll left that could trigger them.
 *
 * ## Usage
 *
 * ```js
 * const dispose = watchReveal(".card", {
 *   from: { autoAlpha: 0, y: 22 },
 *   to:   { autoAlpha: 1, y: 0 },
 *   duration: 0.6,
 *   stagger: 0.05,
 * });
 * ```
 */

import { gsap, EASE } from "../core/gsap-setup.js";
import { rafThrottle } from "../core/dom.js";

/**
 * @typedef {Object} WatchRevealOptions
 * @property {gsap.TweenVars} [from]      Start state applied immediately.
 * @property {gsap.TweenVars} [to]        End state.
 * @property {number} [duration=0.7]
 * @property {string} [ease]
 * @property {number|Object} [stagger=0]
 * @property {number} [delay=0]
 * @property {string} [rootMargin="0px 0px -12% 0px"] Observer margin: the trigger
 *   line, as a share of the viewport. `-12%` on the bottom edge reveals an
 *   element once it is roughly 88% of the way up the screen.
 * @property {number|number[]} [threshold=0]
 * @property {boolean} [animateInView=true] Animate targets that are already in
 *   view, rather than jumping them straight to the end state.
 */

const DEFAULTS = {
  from: { autoAlpha: 0, y: 22 },
  to: { autoAlpha: 1, y: 0 },
  duration: 0.7,
  ease: EASE.out,
  stagger: 0,
  delay: 0,
  rootMargin: "0px 0px -12% 0px",
  threshold: 0,
  animateInView: true,
};

/**
 * Reveal targets on scroll without ever leaving one invisible.
 *
 * @param {string|Element|Element[]} targets
 * @param {WatchRevealOptions} [options]
 * @returns {() => void} Teardown.
 */
export function watchReveal(targets, options = {}) {
  const config = { ...DEFAULTS, ...options };
  const elements = normalise(targets);
  if (!elements.length) return () => {};

  // Allow the ScrollTrigger-style `start: "top 88%"` shorthand so call sites can
  // express the trigger line in the vocabulary they already use.
  if (options.start && !options.rootMargin) {
    const derived = startToRootMargin(options.start);
    if (derived) config.rootMargin = derived;
  }

  const viewportHeight = window.innerHeight || 0;
  /** @type {Element[]} */
  const passed = [];
  /** @type {Element[]} */
  const candidates = [];

  /* --- Start state ------------------------------------------------------- */

  gsap.set(elements, { ...config.from, willChange: "transform, opacity" });

  const settle = (batch = elements) => {
    gsap.to(batch, {
      ...config.to,
      duration: config.duration,
      ease: config.ease,
      stagger: config.stagger,
      delay: config.delay,
      overwrite: true,
      onComplete: () => gsap.set(batch, { willChange: "auto" }),
    });
  };

  const jump = (batch) => {
    if (!batch.length) return;
    gsap.set(batch, { ...config.to, clearProps: "willChange" });
  };

  const finish = () => {
    gsap.killTweensOf(elements);
    gsap.set(elements, { clearProps: "all" });
  };

  /* --- Already above the viewport ---------------------------------------- */

  elements.forEach((element) => {
    const rect = element.getBoundingClientRect();
    /*
     * `rect.bottom < 0` means the element sits entirely above the scroll
     * position. That happens on a deep link, or when a component is initialised
     * after the page has already moved. No scroll could ever trigger it, so it
     * is finished immediately rather than left invisible.
     */
    if (rect.bottom < 0) passed.push(element);
    else if (rect.bottom <= viewportHeight * 0.05) passed.push(element);
    else candidates.push(element);
  });

  jump(passed);

  if (!candidates.length) return finish;

  /* --- Observe ----------------------------------------------------------- */

  if (typeof IntersectionObserver !== "function") {
    // No observer support: reveal everything rather than risk hiding content.
    if (config.animateInView) settle(candidates);
    else jump(candidates);
    return finish;
  }

  /** Everything still waiting to be revealed. Pruned as elements appear. */
  const pending = new Set(candidates);

  const observer = new IntersectionObserver(
    (entries) => {
      /** @type {Element[]} */
      const entering = [];

      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        // One reveal per element: stop watching as soon as it is queued.
        observer.unobserve(entry.target);
        pending.delete(entry.target);
        entering.push(entry.target);
      });

      if (!entering.length) return;

      // Preserve document order so a stagger always reads top-to-bottom, even
      // when several elements report in the same batch.
      const order = new Map(candidates.map((element, index) => [element, index]));
      entering.sort((a, b) => (order.get(a) ?? 0) - (order.get(b) ?? 0));

      if (config.animateInView) settle(entering);
      else jump(entering);
    },
    { root: null, rootMargin: config.rootMargin, threshold: config.threshold }
  );

  candidates.forEach((element) => observer.observe(element));

  /*
   * Safety net.
   *
   * IntersectionObserver samples geometry per frame, so an element that flies
   * through the viewport between two frames — a trackpad flick, a programmatic
   * jump, an anchor landing — may never be reported as intersecting. Anything
   * still pending that now sits entirely above the viewport is therefore
   * finished off directly. The listener is rAF-throttled and removes itself from
   * consideration as soon as the pending set is empty, so it costs nothing once
   * the page has been read.
   */
  const sweep = rafThrottle(() => {
    if (!pending.size) return;

    /** @type {Element[]} */
    const missed = [];

    pending.forEach((element) => {
      if (element.getBoundingClientRect().bottom <= 0) missed.push(element);
    });

    if (!missed.length) return;

    missed.forEach((element) => {
      pending.delete(element);
      observer.unobserve(element);
    });

    // These are above the viewport: animating them would be invisible work.
    jump(missed);
  });

  window.addEventListener("scroll", sweep, { passive: true });
  window.addEventListener("resize", sweep, { passive: true });

  return () => {
    observer.disconnect();
    window.removeEventListener("scroll", sweep);
    window.removeEventListener("resize", sweep);
    finish();
  };
}

/**
 * Translate a ScrollTrigger `start: "top 88%"` value into an equivalent
 * IntersectionObserver `rootMargin`.
 *
 * `"top 88%"` means "fire when the element's top edge crosses 88% of the
 * viewport height", which is the same line as shrinking the observer root's
 * bottom edge by 12%.
 *
 * @param {string} start
 * @returns {string|null} A rootMargin, or `null` when the value is not understood.
 */
export function startToRootMargin(start) {
  const match = /^top\s+(\d+(?:\.\d+)?)\s*%$/.exec(String(start).trim());
  if (!match) return null;
  const percent = Math.min(Math.max(Number(match[1]), 0), 100);
  return `0px 0px ${(percent - 100).toFixed(2)}% 0px`;
}

/**
 * Coerce a selector, element or array into an array of elements.
 *
 * @param {string|Element|Element[]} targets
 * @returns {Element[]}
 */
function normalise(targets) {
  if (typeof targets === "string") return Array.from(document.querySelectorAll(targets));
  if (targets instanceof Element) return [targets];
  if (Array.isArray(targets)) return targets.filter((target) => target instanceof Element);
  return targets ? [targets] : [];
}
